import { useAppConfig } from "@core/composables/data/useAppConfig";
import { canAccessYouparkSection } from "@core/utils/accessPolicy";
import type { RouteRecordRaw } from "vue-router";

import { lotConfigurationTabKeys, type LotConfigurationTabKey } from "@common/youpark/lot-configuration-tabs";

import { getRouteParam, getRouteParamOrEmpty, resolveOrganizationId } from "../utils";

import { youparkService } from "@/services/youpark/service";

const lotConfigurationView = () =>
  import(
    /* webpackChunkName: "dashboard-youpark-admin" */ "../../components/youpark/lot-configuration/LotConfigurationView.vue"
  );

export const lotConfigurationTabRouteByKey = {
  settings: {
    path: "settings",
    name: "youpark-customer-admin-locations-settings"
  },
  priceSetup: {
    path: "price-setup",
    name: "youpark-customer-admin-locations-price-setup"
  },
  autopass: {
    path: "autopass",
    name: "youpark-customer-admin-locations-autopass"
  },
  display: {
    path: "display",
    name: "youpark-customer-admin-locations-display"
  },
  contactpersons: {
    path: "contact-persons",
    name: "youpark-customer-admin-locations-contactpersons"
  },
  cameras: {
    path: "cameras",
    name: "youpark-customer-admin-locations-cameras"
  },
  connections: {
    path: "connections",
    name: "youpark-customer-admin-locations-connections"
  },
  contract: {
    path: "contract",
    name: "youpark-customer-admin-locations-contract"
  },
  businessmodel: {
    path: "business-model",
    name: "youpark-customer-admin-locations-businessmodel"
  }
} satisfies Record<LotConfigurationTabKey, { path: string; name: string }>;

const lotConfigurationTabRoutes: RouteRecordRaw[] = lotConfigurationTabKeys.map(tab => {
  const routeInfo = lotConfigurationTabRouteByKey[tab];

  return {
    path: `customer-administration/locations/${routeInfo.path}`,
    name: routeInfo.name,
    component: lotConfigurationView,
    props: route => ({
      organizationId: resolveOrganizationId(route),
      tab
    }),
    meta: {
      titleKey: "pageTitle.youparkCustomerAdminLocations",
      viewGroup: "youpark-customer-admin-locations",
      viewGroupParams: ["organizationId"]
    }
  };
});

const youparkChildRoutes: RouteRecordRaw[] = [
  {
    path: "",
    name: "youpark",
    component: () =>
      import(/* webpackChunkName: "dashboard-youpark" */ "../../components/youpark/visits/VisitsView.vue"),
    // Route guard now skips the auto-redirect to Insights when navigation originates from another youpark page,
    // so the "Visit" menu entry opens Visits instead of bouncing back.
    beforeEnter: async (to, from) => {
      const organizationId = getRouteParamOrEmpty(to, "organizationId");
      if (!organizationId) {
        return true;
      }
      const fromYouparkSection = typeof from.name === "string" && from.name.startsWith("youpark");
      if (fromYouparkSection) {
        return true;
      }
      try {
        const configuration = await youparkService.fetchConfigurationSummary(organizationId);
        if (configuration.success && configuration.data?.allowBusinessInsights) {
          return { name: "youpark-insights", params: { organizationId } };
        }
        return true;
      } catch {
        return true;
      }
    },
    meta: { titleKey: "pageTitle.youpark", viewGroup: "youpark", viewGroupParams: ["organizationId"] }
  },
  {
    path: "customer-register",
    name: "youpark-customer-register",
    component: () =>
      import(
        /* webpackChunkName: "dashboard-youpark-register" */ "../../components/youpark/customer-register/CustomerRegisterView.vue"
      ),
    props: route => ({
      organizationId: getRouteParamOrEmpty(route, "organizationId")
    }),
    meta: {
      titleKey: "pageTitle.youparkCustomerRegister",
      viewGroup: "youpark-customer-register",
      viewGroupParams: ["organizationId"]
    }
  },
  {
    path: "customer-register/subscription/:subscriptionId/edit",
    name: "youpark-customer-register-subscription-edit",
    component: () =>
      import(
        /* webpackChunkName: "dashboard-youpark-register" */ "../../components/youpark/customer-register/CustomerSubscriptionEditView.vue"
      ),
    props: route => ({
      organizationId: getRouteParamOrEmpty(route, "organizationId"),
      subscriptionId: getRouteParamOrEmpty(route, "subscriptionId")
    }),
    meta: {
      titleKey: "pageTitle.youparkCustomerRegisterEdit",
      viewGroup: "youpark-customer-register",
      viewGroupParams: ["organizationId", "subscriptionId"]
    }
  },
  {
    path: "customer-register/subscription/:subscriptionId/vehicles",
    name: "youpark-customer-register-subscription-vehicles",
    component: () =>
      import(
        /* webpackChunkName: "dashboard-youpark-register" */ "../../components/youpark/customer-register/CustomerSubscriptionVehiclesView.vue"
      ),
    props: route => ({
      organizationId: getRouteParamOrEmpty(route, "organizationId"),
      subscriptionId: getRouteParamOrEmpty(route, "subscriptionId")
    }),
    meta: {
      titleKey: "pageTitle.youparkCustomerRegisterVehicles",
      viewGroup: "youpark-customer-register",
      viewGroupParams: ["organizationId", "subscriptionId"]
    }
  },
  {
    path: "customer-register/bulk-edit/:templateId",
    name: "youpark-customer-register-bulk-edit",
    component: () =>
      import(
        /* webpackChunkName: "dashboard-youpark-register" */ "../../components/youpark/customer-register/CustomerSubscriptionBulkEditView.vue"
      ),
    props: route => ({
      organizationId: getRouteParamOrEmpty(route, "organizationId"),
      templateId: getRouteParamOrEmpty(route, "templateId")
    }),
    meta: {
      titleKey: "pageTitle.youparkCustomerRegisterBulkEdit",
      viewGroup: "youpark-customer-register",
      viewGroupParams: ["organizationId", "templateId"]
    }
  },
  {
    path: "subscriptions",
    name: "youpark-subscriptions",
    component: () =>
      import(
        /* webpackChunkName: "dashboard-youpark-templates" */ "../../components/youpark/template-subscriptions/TemplateSubscriptionsView.vue"
      ),
    props: route => ({
      organizationId: getRouteParamOrEmpty(route, "organizationId")
    }),
    meta: {
      titleKey: "pageTitle.youparkSubscriptions",
      viewGroup: "youpark-subscriptions",
      viewGroupParams: ["organizationId"]
    }
  },
  {
    path: "subscriptions/new",
    name: "youpark-subscriptions-new",
    component: () =>
      import(
        /* webpackChunkName: "dashboard-youpark-templates" */ "../../components/youpark/template-subscriptions/TemplateSubscriptionEditorView.vue"
      ),
    props: route => ({
      organizationId: getRouteParamOrEmpty(route, "organizationId"),
      templateId: ""
    }),
    meta: {
      titleKey: "pageTitle.youparkSubscriptions",
      viewGroup: "youpark-subscriptions",
      viewGroupParams: ["organizationId"]
    }
  },
  {
    path: "subscriptions/:templateId/edit",
    name: "youpark-subscriptions-edit",
    component: () =>
      import(
        /* webpackChunkName: "dashboard-youpark-templates" */ "../../components/youpark/template-subscriptions/TemplateSubscriptionEditorView.vue"
      ),
    props: route => ({
      organizationId: getRouteParamOrEmpty(route, "organizationId"),
      templateId: getRouteParamOrEmpty(route, "templateId")
    }),
    meta: {
      titleKey: "pageTitle.youparkSubscriptions",
      viewGroup: "youpark-subscriptions",
      viewGroupParams: ["organizationId", "templateId"]
    }
  },
  {
    path: "invoices",
    name: "youpark-invoices",
    component: () =>
      import(/* webpackChunkName: "dashboard-youpark-invoices" */ "../../components/youpark/invoices/InvoicesView.vue"),
    props: route => ({
      titleKey: "youpark.menu.invoices",
      iconKey: "invoices",
      organizationId: getRouteParamOrEmpty(route, "organizationId")
    }),
    meta: {
      titleKey: "pageTitle.youparkInvoices",
      viewGroup: "youpark-invoices",
      viewGroupParams: ["organizationId"]
    }
  },
  {
    path: "invoices/:invoiceId",
    name: "youpark-invoice-detail",
    component: () =>
      import(
        /* webpackChunkName: "dashboard-youpark-invoices" */ "../../components/youpark/invoices/InvoiceDetailView.vue"
      ),
    props: route => ({
      organizationId: getRouteParamOrEmpty(route, "organizationId"),
      invoiceId: getRouteParamOrEmpty(route, "invoiceId")
    }),
    meta: {
      titleKey: "pageTitle.youparkInvoices",
      viewGroup: "youpark-invoices",
      viewGroupParams: ["organizationId"]
    }
  },
  {
    path: "transactions",
    name: "youpark-transactions",
    component: () =>
      import(
        /* webpackChunkName: "dashboard-youpark-invoices" */ "../../components/youpark/transactions/TransactionsView.vue"
      ),
    props: route => ({
      titleKey: "youpark.menu.transactions",
      iconKey: "transactions",
      organizationId: getRouteParamOrEmpty(route, "organizationId")
    }),
    meta: {
      titleKey: "pageTitle.youparkTransactions",
      viewGroup: "youpark-transactions",
      viewGroupParams: ["organizationId"]
    }
  },
  {
    path: "credit",
    name: "youpark-credit",
    component: () =>
      import(/* webpackChunkName: "dashboard-youpark-credit" */ "../../components/youpark/credit/CreditView.vue"),
    props: route => ({
      organizationId: getRouteParamOrEmpty(route, "organizationId")
    }),
    meta: {
      titleKey: "pageTitle.youparkCredit",
      viewGroup: "youpark-credit",
      viewGroupParams: ["organizationId"]
    }
  },
  {
    path: "booking-reservations",
    name: "youpark-booking-reservations",
    component: () =>
      import(
        /* webpackChunkName: "dashboard-youpark-booking-reservations" */ "../../components/youpark/booking-reservations/BookingReservationsView.vue"
      ),
    props: route => ({
      organizationId: getRouteParamOrEmpty(route, "organizationId")
    }),
    meta: {
      titleKey: "pageTitle.youparkBookingReservations",
      viewGroup: "youpark-booking-reservations",
      viewGroupParams: ["organizationId"]
    }
  },
  {
    path: "free-visits",
    name: "youpark-free-visits",
    component: () =>
      import(
        /* webpackChunkName: "dashboard-youpark-freevisits" */ "../../components/youpark/free-visits/FreeVisitsView.vue"
      ),
    props: route => ({
      organizationId: getRouteParamOrEmpty(route, "organizationId")
    }),
    meta: {
      titleKey: "pageTitle.youparkFreeVisits",
      viewGroup: "youpark-free-visits",
      viewGroupParams: ["organizationId"]
    }
  },
  {
    path: "free-visits/new",
    name: "youpark-free-visits-new",
    component: () =>
      import(
        /* webpackChunkName: "dashboard-youpark-freevisits" */ "../../components/youpark/free-visits/FreeVisitEditorView.vue"
      ),
    props: route => ({
      organizationId: getRouteParamOrEmpty(route, "organizationId")
    }),
    meta: {
      titleKey: "pageTitle.youparkFreeVisitNew",
      viewGroup: "youpark-free-visits",
      viewGroupParams: ["organizationId"]
    }
  },
  {
    path: "free-visits/:visitId/edit",
    name: "youpark-free-visit-edit",
    component: () =>
      import(
        /* webpackChunkName: "dashboard-youpark-freevisits" */ "../../components/youpark/free-visits/FreeVisitEditorView.vue"
      ),
    props: route => ({
      organizationId: getRouteParamOrEmpty(route, "organizationId"),
      visitId: getRouteParamOrEmpty(route, "visitId")
    }),
    meta: {
      titleKey: "pageTitle.youparkFreeVisitEdit",
      viewGroup: "youpark-free-visits",
      viewGroupParams: ["organizationId", "visitId"]
    }
  },
  {
    path: "free-visits/import",
    name: "youpark-free-visits-import",
    component: () =>
      import(
        /* webpackChunkName: "dashboard-youpark-freevisits" */ "../../components/youpark/free-visits/FreeVisitImportView.vue"
      ),
    props: route => ({
      organizationId: getRouteParamOrEmpty(route, "organizationId")
    }),
    meta: {
      titleKey: "pageTitle.youparkFreeVisitImport",
      viewGroup: "youpark-free-visits",
      viewGroupParams: ["organizationId"]
    }
  },
  {
    path: "reports",
    name: "youpark-reports",
    component: () =>
      import(/* webpackChunkName: "dashboard-youpark-insights" */ "../../components/youpark/reports/ReportsView.vue"),
    props: route => ({
      organizationId: getRouteParamOrEmpty(route, "organizationId")
    }),
    meta: {
      titleKey: "pageTitle.youparkReports",
      viewGroup: "youpark-reports",
      viewGroupParams: ["organizationId"]
    }
  },
  {
    path: "insights",
    name: "youpark-insights",
    component: () =>
      import(
        /* webpackChunkName: "dashboard-youpark-insights" */ "../../components/youpark/business-insights/BusinessInsightsView.vue"
      ),
    props: route => ({
      organizationId: getRouteParamOrEmpty(route, "organizationId")
    }),
    meta: {
      titleKey: "pageTitle.youparkInsights",
      viewGroup: "youpark-insights",
      viewGroupParams: ["organizationId"]
    }
  },
  {
    path: "business-insights",
    name: "youpark-business-insights",
    component: () =>
      import(
        /* webpackChunkName: "dashboard-youpark-insights" */ "../../components/youpark/business-insights/BusinessInsightsView.vue"
      ),
    props: route => ({
      organizationId: getRouteParamOrEmpty(route, "organizationId")
    }),
    meta: {
      titleKey: "pageTitle.youparkBusinessInsights",
      viewGroup: "youpark-insights",
      viewGroupParams: ["organizationId"]
    }
  },
  {
    path: "todos",
    name: "youpark-todos",
    component: () =>
      import(/* webpackChunkName: "dashboard-youpark-todos" */ "../../components/youpark/todos/TodosView.vue"),
    props: route => ({
      organizationId: getRouteParamOrEmpty(route, "organizationId")
    }),
    meta: { titleKey: "pageTitle.youparkTodos", viewGroup: "youpark-todos", viewGroupParams: ["organizationId"] }
  },
  {
    path: "todos/:todoId/:action",
    name: "youpark-todo-process",
    component: () =>
      import(/* webpackChunkName: "dashboard-youpark-todos" */ "../../components/youpark/todos/TodoProcessView.vue"),
    props: route => ({
      organizationId: getRouteParamOrEmpty(route, "organizationId"),
      todoId: getRouteParamOrEmpty(route, "todoId"),
      action: getRouteParam(route, "action") === "decline" ? "decline" : "confirm"
    }),
    meta: {
      titleKey: "pageTitle.youparkTodos",
      viewGroup: "youpark-todos",
      viewGroupParams: ["organizationId", "todoId", "action"]
    }
  },
  {
    path: "access",
    name: "youpark-access",
    component: () =>
      import(/* webpackChunkName: "dashboard-youpark-access" */ "../../components/youpark/access/AccessView.vue"),
    props: route => ({
      organizationId: getRouteParamOrEmpty(route, "organizationId"),
      tab: "visits" as const
    }),
    meta: { titleKey: "pageTitle.youparkAccess", viewGroup: "youpark-access", viewGroupParams: ["organizationId"] }
  },
  {
    path: "access/visits",
    name: "youpark-access-visits",
    component: () =>
      import(/* webpackChunkName: "dashboard-youpark-access" */ "../../components/youpark/access/AccessView.vue"),
    props: route => ({
      organizationId: getRouteParamOrEmpty(route, "organizationId"),
      tab: "visits" as const
    }),
    meta: {
      titleKey: "pageTitle.youparkAccessVisits",
      viewGroup: "youpark-access",
      viewGroupParams: ["organizationId"]
    }
  },
  {
    path: "access/audits",
    name: "youpark-access-audits",
    component: () =>
      import(/* webpackChunkName: "dashboard-youpark-access" */ "../../components/youpark/access/AccessView.vue"),
    props: route => ({
      organizationId: getRouteParamOrEmpty(route, "organizationId"),
      tab: "audits" as const
    }),
    meta: {
      titleKey: "pageTitle.youparkAccessAudits",
      viewGroup: "youpark-access",
      viewGroupParams: ["organizationId"]
    }
  },
  {
    path: "customer-administration",
    name: "youpark-customer-admin",
    redirect: to => ({ name: "youpark-customer-admin-locations", params: to.params }),
    meta: {
      titleKey: "pageTitle.youparkCustomerAdmin",
      viewGroup: "youpark-customer-admin",
      viewGroupParams: ["organizationId"]
    }
  },
  {
    path: "customer-administration/locations",
    name: "youpark-customer-admin-locations",
    redirect: to => ({ name: lotConfigurationTabRouteByKey.settings.name, params: to.params, query: to.query }),
    meta: {
      titleKey: "pageTitle.youparkCustomerAdminLocations",
      viewGroup: "youpark-customer-admin-locations",
      viewGroupParams: ["organizationId"]
    }
  },
  ...lotConfigurationTabRoutes,
  {
    path: "customer-administration/payment-kiosks/:applicationId?",
    name: "youpark-customer-admin-payment-kiosks",
    component: () =>
      import(
        /* webpackChunkName: "dashboard-youpark-admin" */ "../../components/youpark/customer-admin/CustomerAdminPaymentKiosksView.vue"
      ),
    props: route => ({
      organizationId: getRouteParamOrEmpty(route, "organizationId"),
      applicationId: getRouteParam(route, "applicationId")
    }),
    meta: {
      titleKey: "pageTitle.youparkCustomerAdminPaymentKiosks",
      viewGroup: "youpark-customer-admin-payment-kiosks",
      viewGroupParams: ["organizationId", "applicationId"]
    }
  },
  {
    path: "customer-administration/invoicing/:settingsId?",
    name: "youpark-customer-admin-invoicing",
    component: () =>
      import(
        /* webpackChunkName: "dashboard-youpark-admin" */ "../../components/youpark/customer-admin/CollectionProviderSettingsView.vue"
      ),
    props: route => ({
      organizationId: getRouteParamOrEmpty(route, "organizationId"),
      settingsId: getRouteParam(route, "settingsId")
    }),
    meta: {
      titleKey: "pageTitle.youparkCustomerAdminInvoicing",
      viewGroup: "youpark-customer-admin-invoicing",
      viewGroupParams: ["organizationId", "settingsId"]
    }
  },
  {
    path: "customer-administration/clusters",
    name: "youpark-customer-admin-clusters",
    component: () =>
      import(
        /* webpackChunkName: "dashboard-youpark-admin" */ "../../components/youpark/customer-admin/clusters/CustomerAdminClustersView.vue"
      ),
    props: route => ({
      organizationId: getRouteParamOrEmpty(route, "organizationId")
    }),
    meta: {
      titleKey: "pageTitle.youparkCustomerAdminClusters",
      viewGroup: "youpark-customer-admin-clusters",
      viewGroupParams: ["organizationId"]
    }
  },
  {
    path: "customer-administration/areas",
    name: "youpark-customer-admin-areas",
    component: () =>
      import(
        /* webpackChunkName: "dashboard-youpark-admin" */ "../../components/youpark/customer-admin/CustomerAdminAreasView.vue"
      ),
    props: route => ({
      organizationId: getRouteParamOrEmpty(route, "organizationId")
    }),
    meta: {
      titleKey: "pageTitle.youparkCustomerAdminAreas",
      viewGroup: "youpark-customer-admin-areas",
      viewGroupParams: ["organizationId"]
    }
  },
  {
    path: "customer-administration/access/:applicationId?",
    name: "youpark-customer-admin-access",
    component: () =>
      import(
        /* webpackChunkName: "dashboard-youpark-admin" */ "../../components/youpark/customer-admin/CustomerAdminAccessConfigurationView.vue"
      ),
    props: route => ({
      organizationId: getRouteParamOrEmpty(route, "organizationId"),
      applicationId: getRouteParam(route, "applicationId")
    }),
    meta: {
      titleKey: "pageTitle.youparkCustomerAdminAccess",
      viewGroup: "youpark-customer-admin-access",
      viewGroupParams: ["organizationId", "applicationId"]
    }
  },
  {
    path: "customer-administration/free-visit-kiosks/:applicationId?",
    name: "youpark-customer-admin-free-visit-kiosks",
    component: () =>
      import(
        /* webpackChunkName: "dashboard-youpark-admin" */ "../../components/youpark/customer-admin/CustomerAdminFreeVisitKiosksView.vue"
      ),
    props: route => ({
      organizationId: getRouteParamOrEmpty(route, "organizationId"),
      applicationId: getRouteParam(route, "applicationId")
    }),
    meta: {
      titleKey: "pageTitle.youparkCustomerAdminFreeVisitKiosks",
      viewGroup: "youpark-customer-admin-free-visit-kiosks",
      viewGroupParams: ["organizationId", "applicationId"]
    }
  },
  {
    path: "customer-administration/fees",
    name: "youpark-customer-admin-fees",
    component: () =>
      import(
        /* webpackChunkName: "dashboard-youpark-admin" */ "../../components/youpark/customer-admin/CustomerAdminFeesView.vue"
      ),
    props: route => ({
      organizationId: getRouteParamOrEmpty(route, "organizationId")
    }),
    meta: {
      titleKey: "pageTitle.youparkCustomerAdminFees",
      viewGroup: "youpark-customer-admin-fees",
      viewGroupParams: ["organizationId"]
    }
  }
];

export const youparkRoutes: RouteRecordRaw[] = [
  {
    path: "/youpark/:organizationId",
    component: () => import(/* webpackChunkName: "dashboard-youpark" */ "../../components/youpark/Layout.vue"),
    props: route => ({
      organizationId: getRouteParamOrEmpty(route, "organizationId")
    }),
    meta: { viewGroup: "youpark", viewGroupParams: ["organizationId"] },
    beforeEnter: to => {
      const { config } = useAppConfig();
      const organizationId = getRouteParam(to, "organizationId");
      if (!canAccessYouparkSection(config, organizationId)) {
        return { name: "error-forbidden" };
      }
      return true;
    },
    children: youparkChildRoutes
  }
];
